/* eslint-disable react/function-component-definition */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import { Feature, useFeature } from "flagged";

// MUI Components
import { Card, Divider, FormControl } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";

// Custom Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import MDBox from "components/MDBox";
import LocationCalculation from "examples/Drawers/ProjectMap/index";
import SnagViewModal from "layouts/snag/SnagViewModal";
import SnagFormModal from "layouts/snag/SnagFormModal";
import MDTypography from "components/MDTypography";
import ResetFilterButton from "components/Buttons/ResetButton";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";
import SearchableMultiSelectDropdown from "components/Dropdown/SearchableMultiSelectDropdown";

import { useDispatch, useSelector } from "react-redux";
import { openSnackbar } from "redux/Slice/Notification";
import { projectListThunk } from "redux/Thunks/Filter";
import getProjectMapData from "redux/Thunks/ProjectMap";
import {
  assetsUpdateThunk,
  locationListByIdThunk,
  locationTeamsUpdateThunk,
  teamThunk,
} from "redux/Thunks/FieldsData";
import { setStoreFilters, resetFilters } from "redux/Slice/Filter";
import { getProjectMapSnags, getSnagById } from "redux/Thunks/Snag";
import { getFormattedProjectName } from "utils/methods/methods";

import Constants, {
  PageTitles,
  Icons,
  Colors,
  FeatureTags,
  defaultData,
  FiltersModuleName,
} from "utils/Constants";

import ProjectMapView from "./ProjectMapView";

const PROJECT_MAP_FILTERS = [
  {
    inputLabel: FiltersModuleName.PROJECT,
    list: [FiltersModuleName.PROJECT_TRACKER_FILTERS_TITLE_OBJ],
    selectedValue: FiltersModuleName.SELECT_IN_SMALL_CASE,
    textTransform: "none",
  },
];

const DEFAULT_ARC_COLOR = Colors.GREY?.trim() || Colors.GREY;
const DEFAULT_INSTALLED_ARC_COLOR = Colors.ARC_INSTALLED_GREEN;
const DEFAULT_TEAM_BADGE_COLOR = "#D0D5DD";

// Utility functions
const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return value.toString();
  return value[Constants.MONGOOSE_ID]?.toString?.() || value.id?.toString?.() || "";
};

const parseCoordinate = (value) => {
  if (value == null) return null;
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeColorValue = (value) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed || "";
};

const isDefaultArcColor = (value) => {
  const normalized = normalizeColorValue(value).toLowerCase();
  if (!normalized) return true;
  if (normalized === "grey" || normalized === "gray") return true;
  return normalized === normalizeColorValue(DEFAULT_ARC_COLOR).toLowerCase();
};

const extractProjectStringColor = (asset) => {
  const projectString = asset?.projectString;
  return normalizeColorValue(projectString?.colorCode || projectString?.color);
};

const extractAssetColor = (asset) => normalizeColorValue(asset?.colorCode);
const normalizeTeamColor = (value) => {
  const normalized = normalizeColorValue(value);
  if (!normalized) return DEFAULT_TEAM_BADGE_COLOR;
  return normalized.startsWith("#") ? normalized : `#${normalized}`;
};

const normalizeTeamIds = (teamIds) => {
  if (!Array.isArray(teamIds)) return [];

  const normalizedTeamIds = [];

  teamIds
    .map((teamId) => getEntityId(teamId))
    .filter(Boolean)
    .forEach((teamId) => {
      if (!normalizedTeamIds.includes(teamId)) {
        normalizedTeamIds.push(teamId);
      }
    });

  return normalizedTeamIds;
};

const buildTeamOptions = (teams = []) =>
  teams.map((team) => ({
    ...team,
    title: team?.teamsWfmName || team?.title || team?.name || "",
    name: team?.teamsWfmName || team?.name || team?.title || "",
    color: normalizeTeamColor(team?.color).replace(/^#/, ""),
  }));

const buildTeamLookup = (teams = []) =>
  teams.reduce((accumulator, team) => {
    const teamId = getEntityId(team);
    if (!teamId) {
      return accumulator;
    }

    return {
      ...accumulator,
      [teamId]: {
        ...team,
        color: normalizeTeamColor(team?.color),
      },
    };
  }, {});

const mapTeamIdsToTeams = (teamIds = [], teamLookup = {}) =>
  normalizeTeamIds(teamIds)
    .map((teamId) => teamLookup[teamId])
    .filter(Boolean);

const mergeLocationTeamData = (locations = [], locationMeta = [], teams = []) => {
  if (!Array.isArray(locations)) return [];

  const teamLookup = buildTeamLookup(teams);

  const locationLookup = {};
  (Array.isArray(locationMeta) ? locationMeta : []).forEach((location) => {
    locationLookup[getEntityId(location)] = normalizeTeamIds(location?.teamIds);
  });

  return locations.map((location) => {
    const locationId = getEntityId(location?.locationId || location?.[Constants.MONGOOSE_ID]);
    const assignedTeamIds = locationLookup[locationId] || [];

    return {
      ...location,
      locationId,
      teamIds: assignedTeamIds,
      assignedTeams: mapTeamIdsToTeams(assignedTeamIds, teamLookup),
    };
  });
};

const groupSnagsByLocation = (snags = []) =>
  (Array.isArray(snags) ? snags : []).reduce((accumulator, snag) => {
    const locationId = getEntityId(snag?.location);
    if (!locationId) return accumulator;
    return {
      ...accumulator,
      [locationId]: [...(accumulator[locationId] || []), snag],
    };
  }, {});

const attachSnagsToLocations = (locations, snags) => {
  if (!Array.isArray(locations)) return locations;
  const snagLookup = groupSnagsByLocation(snags);
  return locations.map((location) => ({
    ...location,
    snags: snagLookup[getEntityId(location?.locationId)] || [],
  }));
};

const resolveInstalledColor = (asset) => {
  const projectStringColor = extractProjectStringColor(asset);
  const assetColor = extractAssetColor(asset);
  const meaningfulAssetColor = assetColor && !isDefaultArcColor(assetColor) ? assetColor : "";

  if (meaningfulAssetColor && !projectStringColor) {
    return Colors.DARK_GREEN;
  }

  return projectStringColor || meaningfulAssetColor || DEFAULT_INSTALLED_ARC_COLOR;
};

// const getInstallationFlag = (asset) => asset?.colorCode !== Colors.GREY;
const getInstallationFlag = (asset) => (asset?.colorCode ? asset.colorCode !== Colors.GREY : false);

// Same rule as MultiLayerProgressDonutMarker: only locations with coordinates and scopes get a pin
const isRenderableLocation = (location) =>
  Number.isFinite(location?.latitude) &&
  Number.isFinite(location?.longitude) &&
  Array.isArray(location?.scope) &&
  location.scope.length > 0;

const buildArcConnections = (locations) => {
  if (!Array.isArray(locations)) return [];

  const arcs = [];
  // The API lists every asset under both its "from" and "to" location. Build each arc once:
  // duplicate React keys leave an orphaned arc layer on the map when the list changes.
  const seenAssetIds = new Set();
  locations.forEach((location) => {
    if (!Array.isArray(location?.assets)) return;

    location.assets.forEach((asset) => {
      const { fromLocation, toLocation } = asset || {};
      if (!fromLocation || !toLocation) return;

      const assetId = getEntityId(asset);
      if (assetId) {
        if (seenAssetIds.has(assetId)) return;
        seenAssetIds.add(assetId);
      }

      const fromLat = parseCoordinate(fromLocation.latitude);
      const fromLng = parseCoordinate(fromLocation.longitude);
      const toLat = parseCoordinate(toLocation.latitude);
      const toLng = parseCoordinate(toLocation.longitude);

      if (fromLat == null || fromLng == null || toLat == null || toLng == null) return;

      const installedColor = resolveInstalledColor(asset);
      const isCompleted = getInstallationFlag(asset);

      arcs.push({
        from: [fromLat, fromLng],
        to: [toLat, toLng],
        color: installedColor,
        baseColor: DEFAULT_ARC_COLOR,
        completeColor: installedColor,
        isCompleted,
        key: asset?.[Constants.MONGOOSE_ID],
        title: asset?.title,
        installedColor,
        fromLocationId: getEntityId(fromLocation?.id || fromLocation),
        toLocationId: getEntityId(toLocation?.id || toLocation),
      });
    });
  });

  return arcs;
};

const Map = () => {
  const dispatch = useDispatch();
  const projectMapRef = useRef();
  const projectMapAbortRef = useRef(null);
  const latestProjectMapRequestRef = useRef(0);
  const projectMapFilters = useSelector((state) => state.filtersSlice.projectMap);
  const configScreens = useSelector((state) => state.config.screens);

  const initialFilters = projectMapFilters?.length > 0 ? projectMapFilters : PROJECT_MAP_FILTERS;

  const [filters, setFilters] = useState(initialFilters);
  const filtersRef = useRef(initialFilters);
  filtersRef.current = filters;
  const [locationData, setLocationData] = useState({ id: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [viewLocationProgressAnchor, setViewLocationProgressAnchor] = useState({ right: false });
  const [reportRowsData, setReportRowsData] = useState(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [projectTeams, setProjectTeams] = useState([]);
  const [snagView, setSnagView] = useState({ open: false, snagId: "" });
  const [snagForm, setSnagForm] = useState({ open: false, location: null });
  const [snagEdit, setSnagEdit] = useState({ open: false, snag: null });
  const isSnagFeatureEnabled = Boolean(useFeature(FeatureTags.SNAG));

  const projectMapAgreement = useMemo(
    () =>
      configScreens?.find((screen) => screen.id === defaultData.PROJECT_MAP_SCREEN_ID)?.screensInfo
        ?.agreement || {},
    [configScreens]
  );
  const canManageProjectMapTeams = Boolean(
    projectMapAgreement?.create || projectMapAgreement?.update
  );
  const snagAgreement = useMemo(
    () =>
      configScreens?.find((screen) => screen.id === defaultData.SNAG_SCREEN_ID)?.screensInfo
        ?.agreement || {},
    [configScreens]
  );
  const canViewSnags = isSnagFeatureEnabled && Boolean(snagAgreement?.read);
  const canCreateSnags = isSnagFeatureEnabled && Boolean(snagAgreement?.create);
  const canUpdateSnags = isSnagFeatureEnabled && Boolean(snagAgreement?.update);

  const showNotification = useCallback(
    (message, notificationType) => {
      dispatch(openSnackbar({ message, notificationType }));
    },
    [dispatch]
  );

  const cancelProjectMapRequest = useCallback(() => {
    if (projectMapAbortRef.current) {
      projectMapAbortRef.current.abort();
      projectMapAbortRef.current = null;
    }
  }, []);

  // Get Project List
  const getProjectListFunc = useCallback(async () => {
    try {
      const projectList = await dispatch(projectListThunk());
      if (projectList?.payload?.status) {
        const projects = Array.isArray(projectList.payload.data) ? projectList.payload.data : [];

        setFilters((prev) => {
          const previousSelection = prev.find(
            (filter) => filter.inputLabel === FiltersModuleName.PROJECT
          )?.selectedValue;

          const projectOptions = [FiltersModuleName.PROJECT_TRACKER_FILTERS_TITLE_OBJ, ...projects];
          const nextSelectedValue = projects.some(
            (project) => project?.[Constants.MONGOOSE_ID] === previousSelection
          )
            ? previousSelection
            : FiltersModuleName.SELECT_IN_SMALL_CASE;

          const updatedFilters = prev.map((filter) =>
            filter.inputLabel === FiltersModuleName.PROJECT
              ? { ...filter, selectedValue: nextSelectedValue, list: projectOptions }
              : filter
          );

          dispatch(setStoreFilters({ module: FeatureTags.PROJECT_MAP, filters: updatedFilters }));
          return updatedFilters;
        });
      }
    } catch (error) {
      showNotification(Constants.SOMETHING_WENT_WRONG, Constants.NOTIFICATION_ERROR);
    }
  }, [dispatch, showNotification]);

  // Get Project Tracker Data
  const getProjectReportProgress = useCallback(
    async (projectId) => {
      if (!projectId) return;

      cancelProjectMapRequest();

      const controller = new AbortController();
      const requestId = latestProjectMapRequestRef.current + 1;
      latestProjectMapRequestRef.current = requestId;
      projectMapAbortRef.current = controller;
      setLoading(true);

      try {
        const [mapRes, locationRes, teamRes, snagRes] = await Promise.all([
          dispatch(getProjectMapData({ projectId, signal: controller.signal })),
          dispatch(locationListByIdThunk({ id: projectId, signal: controller.signal })),
          dispatch(teamThunk({ id: projectId, signal: controller.signal })),
          canViewSnags
            ? dispatch(getProjectMapSnags({ projectId, signal: controller.signal }))
            : Promise.resolve(null),
        ]);

        if (controller.signal.aborted || latestProjectMapRequestRef.current !== requestId) {
          return;
        }

        const mapRows = mapRes?.payload?.status === 200 ? mapRes?.payload?.data?.data : null;
        const projectLocations = Array.isArray(locationRes?.payload?.data)
          ? locationRes.payload.data
          : [];
        const teams = Array.isArray(teamRes?.payload?.data) ? teamRes.payload.data : [];

        const snags =
          snagRes?.payload?.status === 200 && Array.isArray(snagRes?.payload?.data?.data)
            ? snagRes.payload.data.data
            : [];

        setProjectTeams(teams);
        setReportRowsData(
          attachSnagsToLocations(mergeLocationTeamData(mapRows, projectLocations, teams), snags)
        );
      } catch (error) {
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
          return;
        }

        setProjectTeams([]);
        setReportRowsData(null);
      } finally {
        if (latestProjectMapRequestRef.current === requestId) {
          setLoading(false);
        }

        if (projectMapAbortRef.current === controller) {
          projectMapAbortRef.current = null;
        }
      }
    },
    [cancelProjectMapRequest, canViewSnags, dispatch]
  );

  useEffect(() => {
    getProjectListFunc();
  }, [getProjectListFunc]);

  useEffect(() => {
    const selectedProjectId = filters?.[0]?.selectedValue;
    if (selectedProjectId && selectedProjectId !== FiltersModuleName.SELECT_IN_SMALL_CASE) {
      getProjectReportProgress(selectedProjectId);
    } else {
      cancelProjectMapRequest();
      latestProjectMapRequestRef.current += 1;
      setLoading(false);
      setReportRowsData(null);
      setProjectTeams([]);
    }
  }, [cancelProjectMapRequest, filters, getProjectReportProgress]);

  useEffect(
    () => () => {
      cancelProjectMapRequest();
    },
    [cancelProjectMapRequest]
  );

  const handleFilterChange = (e) => {
    const { value } = e.target;
    setFilters((prev) => {
      const updatedFilters = prev.map((filter) =>
        filter.inputLabel === FiltersModuleName.PROJECT
          ? { ...filter, selectedValue: value || FiltersModuleName.SELECT_IN_SMALL_CASE }
          : filter
      );
      dispatch(setStoreFilters({ module: FeatureTags.PROJECT_MAP, filters: updatedFilters }));
      return updatedFilters;
    });
  };

  const handleResetFilter = () => {
    cancelProjectMapRequest();
    latestProjectMapRequestRef.current += 1;
    setLoading(false);
    setFilters((prev) => {
      const updatedFilters = prev.map((filter) =>
        filter.inputLabel === FiltersModuleName.PROJECT
          ? { ...filter, selectedValue: FiltersModuleName.SELECT_IN_SMALL_CASE }
          : filter
      );
      dispatch(resetFilters({ module: FeatureTags.PROJECT_MAP }));
      return updatedFilters;
    });
    setReportRowsData(null);
    setProjectTeams([]);
    setSelectedLocationIds([]);
  };

  const arcs = useMemo(() => buildArcConnections(reportRowsData), [reportRowsData]);

  /* --------------------------- Location filter --------------------------- */
  const selectedProjectId = filters?.[0]?.selectedValue;

  // Location selection belongs to a project - clear it when the project changes
  useEffect(() => {
    setSelectedLocationIds([]);
  }, [selectedProjectId]);

  const locationFilterOptions = useMemo(
    () =>
      (Array.isArray(reportRowsData) ? reportRowsData : [])
        .filter(isRenderableLocation)
        .map((location) => ({
          [Constants.MONGOOSE_ID]: getEntityId(location?.locationId),
          title: location?.locationName || "",
        }))
        .filter((option) => option[Constants.MONGOOSE_ID]),
    [reportRowsData]
  );

  // Drop selected locations that no longer exist after a reload
  useEffect(() => {
    const availableIds = new Set(
      locationFilterOptions.map((option) => option[Constants.MONGOOSE_ID])
    );
    setSelectedLocationIds((prev) => {
      const next = prev.filter((id) => availableIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [locationFilterOptions]);

  const isLocationFilterActive = selectedLocationIds.length > 0;

  const visibleLocations = useMemo(() => {
    if (!Array.isArray(reportRowsData) || !isLocationFilterActive) return reportRowsData;
    const selectedIds = new Set(selectedLocationIds);
    return reportRowsData.filter((location) => selectedIds.has(getEntityId(location?.locationId)));
  }, [isLocationFilterActive, reportRowsData, selectedLocationIds]);

  // With a location filter, show only connections between selected locations
  const visibleArcs = useMemo(() => {
    if (!isLocationFilterActive) return arcs;
    const selectedIds = new Set(selectedLocationIds);
    return arcs.filter(
      (arc) => selectedIds.has(arc.fromLocationId) && selectedIds.has(arc.toLocationId)
    );
  }, [arcs, isLocationFilterActive, selectedLocationIds]);

  const handleLocationFilterChange = (_name, values) => {
    setSelectedLocationIds(Array.isArray(values) ? values : []);
  };

  const handleArcStatusChange = async ({ id, isComplete, arc }) => {
    if (!id) {
      showNotification(Constants.SOMETHING_WENT_WRONG, Constants.NOTIFICATION_ERROR);
      return { success: false };
    }

    const nextColorCode = isComplete
      ? arc?.completeColor || arc?.installedColor || DEFAULT_INSTALLED_ARC_COLOR
      : DEFAULT_ARC_COLOR;

    let previousAssetState = null;

    const updateAssets = (colorCode, isArcInstalled) => {
      setReportRowsData((prev) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((location) => {
          if (!Array.isArray(location?.assets)) return location;

          const updatedAssets = location.assets.map((asset) => {
            if (asset?.[Constants.MONGOOSE_ID] !== id) return asset;

            if (!previousAssetState) {
              previousAssetState = {
                colorCode: asset.colorCode,
                isArcInstalled: asset.isArcInstalled ?? Boolean(asset.isInstalled),
              };
            }

            return { ...asset, colorCode, isArcInstalled };
          });

          return { ...location, assets: updatedAssets };
        });
      });
    };

    // Optimistically update UI
    updateAssets(nextColorCode, isComplete);

    try {
      const response = await dispatch(
        assetsUpdateThunk({
          id,
          body: { colorCode: nextColorCode },
        })
      );

      if (response?.payload?.status === 200) {
        showNotification(
          isComplete
            ? Constants.PROJECT_MAP_ARC_COMPLETE_SUCCESS
            : Constants.PROJECT_MAP_ARC_REVERT_SUCCESS,
          Constants.NOTIFICATION_SUCCESS
        );
        return { success: true };
      }

      // Revert on failure
      if (previousAssetState) {
        updateAssets(previousAssetState.colorCode, previousAssetState.isArcInstalled);
      }
    } catch (error) {
      // Revert on error
      if (previousAssetState) {
        updateAssets(previousAssetState.colorCode, previousAssetState.isArcInstalled);
      }
    }

    showNotification(Constants.SOMETHING_WENT_WRONG, Constants.NOTIFICATION_ERROR);
    return { success: false };
  };

  const handleLocationTeamsSave = useCallback(
    async ({ locationId, teamIds }) => {
      try {
        const response = await dispatch(locationTeamsUpdateThunk({ id: locationId, teamIds }));

        if (response?.payload?.status === 200) {
          const normalizedTeamIds = normalizeTeamIds(teamIds);
          const teamLookup = buildTeamLookup(projectTeams);

          setReportRowsData((prev) =>
            Array.isArray(prev)
              ? prev.map((location) =>
                  location?.locationId === locationId
                    ? {
                        ...location,
                        teamIds: normalizedTeamIds,
                        assignedTeams: mapTeamIdsToTeams(normalizedTeamIds, teamLookup),
                      }
                    : location
                )
              : prev
          );

          showNotification(
            Constants.PROJECT_MAP_TEAMS_SAVE_SUCCESS,
            Constants.NOTIFICATION_SUCCESS
          );
          return { success: true };
        }
      } catch (error) {
        showNotification(Constants.PROJECT_MAP_TEAMS_SAVE_ERROR, Constants.NOTIFICATION_ERROR);
        return { success: false };
      }

      showNotification(Constants.PROJECT_MAP_TEAMS_SAVE_ERROR, Constants.NOTIFICATION_ERROR);
      return { success: false };
    },
    [dispatch, projectTeams, showNotification]
  );

  // Reload only the snags (badges / popup list) after a snag is added or edited from the map
  const refreshProjectMapSnags = useCallback(async () => {
    const projectId = filters?.[0]?.selectedValue;
    if (!canViewSnags || !projectId || projectId === FiltersModuleName.SELECT_IN_SMALL_CASE) {
      return;
    }

    const snagRes = await dispatch(getProjectMapSnags({ projectId }));
    if (snagRes?.payload?.status !== 200 || !Array.isArray(snagRes?.payload?.data?.data)) return;
    // Ignore the response if the user switched project meanwhile
    if (filtersRef.current?.[0]?.selectedValue !== projectId) return;

    setReportRowsData((prev) => attachSnagsToLocations(prev, snagRes.payload.data.data));
  }, [canViewSnags, dispatch, filters]);

  const selectedProjectOption = useMemo(() => {
    const projectFilter = filters?.[0];
    const project = projectFilter?.list?.find(
      (item) => item?.[Constants.MONGOOSE_ID] === projectFilter?.selectedValue
    );
    return project
      ? {
          [Constants.MONGOOSE_ID]: project[Constants.MONGOOSE_ID],
          title: getFormattedProjectName(project),
        }
      : null;
  }, [filters]);

  const handleAddSnagFromMap = useCallback(({ locationId, locationName }) => {
    setSnagForm({
      open: true,
      location: { [Constants.MONGOOSE_ID]: locationId, title: locationName },
    });
  }, []);

  // Users who can update snags edit the clicked snag directly, others get the read-only details
  const handleSnagClick = useCallback(
    async (snag) => {
      const snagId = snag?.[Constants.MONGOOSE_ID] || "";
      if (!snagId) return;
      if (!canUpdateSnags) {
        setSnagView({ open: true, snagId });
        return;
      }

      const res = await dispatch(getSnagById(snagId));
      const snagDetails = res?.payload?.data?.data;
      if (res?.payload?.status === 200 && snagDetails) {
        setSnagEdit({ open: true, snag: snagDetails });
      } else {
        showNotification(
          res?.payload?.data?.message || Constants.SOMETHING_WENT_WRONG,
          Constants.NOTIFICATION_ERROR
        );
      }
    },
    [canUpdateSnags, dispatch, showNotification]
  );

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {Icons.LOADING2}
        </MDBox>
      );
    }

    const isProjectSelected = filters[0]?.selectedValue !== FiltersModuleName.SELECT_IN_SMALL_CASE;

    if (!reportRowsData || !isProjectSelected) {
      return (
        <Card sx={{ height: "60vh", marginTop: 3 }}>
          <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
            <MDTypography sx={{ fontSize: "25px", fontWeight: "semibold", opacity: "0.8" }}>
              {!isProjectSelected
                ? Constants.PROJECT_MAP_SELECT_PROJECT_PROMPT
                : Constants.PROJECT_MAP_NO_TRACKING_MESSAGE}
            </MDTypography>
          </MDBox>
        </Card>
      );
    }

    return (
      <MDBox
        sx={{ paddingTop: pxToRem(24), paddingBottom: pxToRem(32) }}
        ref={projectMapRef}
        id="pdfTable"
      >
        <ProjectMapView
          locations={visibleLocations}
          arcs={visibleArcs}
          mapRef={projectMapRef}
          onArcStatusChange={handleArcStatusChange}
          teamOptions={buildTeamOptions(projectTeams)}
          onLocationTeamsSave={handleLocationTeamsSave}
          canManageTeams={canManageProjectMapTeams}
          showSnags={canViewSnags}
          canAddSnag={canCreateSnags}
          onAddSnagClick={handleAddSnagFromMap}
          onSnagClick={handleSnagClick}
          totalScopeCount={
            reportRowsData
              ? Math.max(
                  ...reportRowsData.map((loc) => (Array.isArray(loc.scope) ? loc.scope.length : 0)),
                  1
                )
              : 1
          }
          onLocationClick={(loc) => {
            setLocationData({
              id: loc.locationId,
              name: loc.locationName,
              projectId: filters[0].selectedValue,
            });
            setViewLocationProgressAnchor({ right: true });
          }}
        />
      </MDBox>
    );
  };

  const isProjectSelected = filters[0]?.selectedValue !== FiltersModuleName.SELECT_IN_SMALL_CASE;

  return (
    <>
      <DashboardLayout module={defaultData.PROJECT_MAP_SCREEN_ID}>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="space-between">
          <PageTitle title={PageTitles.PROJECT_MAP} />
          <BasicButton
            icon={Icons.RELOAD}
            background={Colors.WHITE}
            border
            color={Colors.BLACK}
            action={getProjectListFunc}
            disabled={!isProjectSelected}
          />
        </MDBox>
        <Divider sx={{ marginTop: 1 }} />
        <Feature name={FeatureTags.PROJECT_MAP}>
          <MDBox display="flex" justifyContent="space-between" mt={0} mx={0} width="100%">
            <MDBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "start",
                alignItems: "flex-end",
              }}
              mt={0}
              mx={0}
              width="100%"
            >
              {filters?.map((val) => (
                <FormControl
                  key={val?.inputLabel}
                  variant="standard"
                  size="medium"
                  style={{ marginTop: "15px", width: 200, marginRight: "15px" }}
                >
                  <CustomAutoComplete
                    label={val?.inputLabel}
                    name={val?.inputLabel}
                    id={val?.inputLabel}
                    getOptionLabel={(option) => option.title || ""}
                    menu={val?.list}
                    value={{
                      title:
                        val?.list.find((item) => item[Constants.MONGOOSE_ID] === val?.selectedValue)
                          ?.title || "",
                    }}
                    handleChange={handleFilterChange}
                    valueStyle={{
                      backgroundColor: Colors.WHITE,
                      height: pxToRem(40),
                      verticalMarginTop: pxToRem(4),
                      menuWidth: 400,
                      inputWidth: 250,
                      padding: pxToRem(1),
                    }}
                    labelStyle={{
                      fontSize: pxToRem(14),
                      fontWeight: 600,
                      color: Colors.BLACK,
                    }}
                    textTransform={val.textTransform}
                  />
                </FormControl>
              ))}
              <FormControl
                variant="standard"
                size="medium"
                style={{ marginTop: "15px", width: 220, marginRight: "15px" }}
              >
                <SearchableMultiSelectDropdown
                  id="project-map-location-filter"
                  name="locations"
                  label={FiltersModuleName.LOCATION}
                  hint={FiltersModuleName.ALL_WITH_FIRST_LETTER_CAPITAL}
                  menu={locationFilterOptions}
                  values={selectedLocationIds}
                  handleChange={handleLocationFilterChange}
                  disabled={!isProjectSelected || locationFilterOptions.length === 0}
                  showBadge
                  searchPlaceholder={`Search ${FiltersModuleName.LOCATION}`}
                  valueStyle={{
                    backgroundColor: Colors.WHITE,
                    height: pxToRem(40),
                    verticalMarginTop: pxToRem(4),
                    menuWidth: 280,
                    menuMaxHeight: 260,
                    inputWidth: 180,
                  }}
                  labelStyle={{
                    fontSize: pxToRem(14),
                    fontWeight: 600,
                    color: Colors.BLACK,
                  }}
                />
              </FormControl>
              <ResetFilterButton handleReset={handleResetFilter} style={{ marginLeft: "1rem" }} />
            </MDBox>
          </MDBox>
          {renderContent()}
        </Feature>
      </DashboardLayout>
      {viewLocationProgressAnchor.right && (
        <LocationCalculation
          viewLocationProgressAnchor={viewLocationProgressAnchor}
          setViewLocationProgressAnchor={setViewLocationProgressAnchor}
          locationData={locationData}
          setLocationData={setLocationData}
          showSnags={canViewSnags}
        />
      )}
      {snagForm.open && (
        <SnagFormModal
          open={snagForm.open}
          handleClose={() => setSnagForm({ open: false, location: null })}
          defaultProject={selectedProjectOption}
          defaultLocation={snagForm.location}
          lockProjectLocation
          onSaved={refreshProjectMapSnags}
        />
      )}
      {snagEdit.open && (
        <SnagFormModal
          open={snagEdit.open}
          handleClose={() => setSnagEdit({ open: false, snag: null })}
          snag={snagEdit.snag}
          defaultProject={selectedProjectOption}
          defaultLocation={snagEdit.snag?.location}
          lockProjectLocation
          onSaved={refreshProjectMapSnags}
        />
      )}
      {snagView.open && (
        <SnagViewModal
          open={snagView.open}
          snagId={snagView.snagId}
          handleClose={() => setSnagView({ open: false, snagId: "" })}
        />
      )}
    </>
  );
};

export default Map;
